// fsProvenance.js — the Files X-ray's data source: the backend `fs` signal.
//
// The Files tab used to reconstruct file activity by HYDRATING every prompt in
// the scan window (one raw-body fetch each) and re-extracting tool calls in the
// browser; line blame ILIKE'd the raw `event_data` blob one day at a time. Both
// are the expensive row scans the `fs` signal exists to replace: it already
// records every file an agent writes (with the effective diff) and reads (with
// numbered observations) as indexed `signal_tags`, so the tree is one
// `fs.write`/`fs.read` read over the window and blame is an `fs.write` TAG
// equality — no hydration, no blob scan.
//
// This module is the client half: the reversed-path tag (byte-parity with the
// kernel), the value→op mapping onto the shape buildFileTree/reconstructFile
// already consume, the per-tenant availability probe (gates the tab like the
// `memories` dim's), and the per-file blame pick. The analytics query methods
// themselves (getFsSignalOps / getFsSignalAvailable) live in apiClient.js.

import {
    buildPromptRootAndFilter,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
} from "./analyticsClient.js";
import { toApiRange } from "./shell.js";
import { authoringOpFromOps } from "./traceLineBlame.js";

// ── reversed-path tag (byte-parity port of the fs signal's `_rpath`) ────────
// The normalized full path with its segments REVERSED (filename first),
// backslashes → forward slashes, empty segments dropped. MUST match the backend
// `_rpath` byte-for-byte so a dashboard tag lookup seeks the exact rows the
// kernel wrote (backend/signals-catalog/fs/signal.py). Pinned by a parity test.
export function rpath(path) {
    const segs = String(path ?? "").replace(/\\/g, "/").split("/").filter((s) => s !== "");
    return segs.length ? segs.reverse().join("/") : String(path ?? "");
}

// ── signal value JSON → the op shape buildFileTree / reconstructFile consume ──
// The fs signal already did the extraction the client's extractFileOps did, so
// each fs.write/fs.read instance value maps directly to one op. Returns null for
// a row we don't reconstruct from (a delete op, an unparseable value, a facet or
// non-instance namespace) — a pure-delete file then just doesn't render, rather
// than showing an op-less tree row (the tree has no delete affordance).
//
// `seq` disambiguates MULTIPLE same-file ops in one event: the kernel emits one
// instance row per op, and signal_tags' dedup identity includes `value`, so a
// Write-then-Edit — or two Edits — to the same file in one exchange arrives as
// two same-tag rows. Without it their keys collide and collectWindowOps'
// `${sessionId}|${key}` dedup silently drops every op after the first.
export function fsValueToOp({ event_id: eventId, namespace, tag, value }, seq = 0) {
    let v;
    try { v = typeof value === "string" ? JSON.parse(value) : value; } catch { return null; }
    if (!v || typeof v !== "object" || !v.path) return null;
    const key = `${eventId}:${namespace}:${tag}:${seq}`;
    if (namespace === "fs.read") {
        const obs = Array.isArray(v.obs) ? v.obs : [];
        return {
            key, kind: "read", path: v.path, ok: true, plus: 0, minus: 0,
            readLines: typeof v.lines === "number" ? v.lines : obs.length,
            obs, obsFull: Boolean(v.obsFull),
        };
    }
    if (namespace === "fs.write") {
        if (v.op === "write") {
            return {
                key, kind: "write", path: v.path, ok: true,
                plus: v.plus ?? 0, minus: 0, readLines: 0,
                content: typeof v.content === "string" ? v.content : "",
                contentTruncated: Boolean(v.truncated),
            };
        }
        if (v.op === "edit") {
            // The kernel stores hunk `old` as null for a pure insertion; the
            // reconstructor wants strings. `replace_all` isn't captured by the
            // signal, so a replace-all edit replays as a single application.
            const hunks = (Array.isArray(v.hunks) ? v.hunks : []).map((h) => ({ old: h.old ?? "", new: h.new ?? "" }));
            return {
                key, kind: "edit", path: v.path, ok: true,
                plus: v.plus ?? 0, minus: v.minus ?? 0, readLines: 0, hunks, replaceAll: false,
            };
        }
        return null; // delete (or unknown op) — no reconstruction contribution.
    }
    return null;
}

// Replay order for same-(event, tag) rows. ClickHouse returns them in NO
// guaranteed order (getFsSignalOps has no orderBy; signal_tags' key ends in
// `value`) and their event_time is identical, so order comes from the value
// itself: the kernel emits a per-event `seq` in every instance value — the
// TRUE tool-call ordinal, shared across fs.read and fs.write (see
// docs/specs/signals-contract.md: consumers must replay by seq, never row
// order). Rows without seq (none should exist — the field predates any
// deployment — but stay defensive) fall back to the read → write → edit
// heuristic matching the dominant agent pattern (observe, author, refine),
// with the raw value string as a stable same-kind tiebreak. reconstructFile
// is stateful (a write replaces content, an edit applies against current
// state), which is why this ordering is load-bearing.
function fsRowOrder(a, b) {
    const t = String(a.event_time || "").localeCompare(String(b.event_time || ""));
    if (t) return t;
    const parsed = (r) => {
        try { return JSON.parse(typeof r.value === "string" ? r.value : "{}"); } catch { return {}; }
    };
    const va = parsed(a);
    const vb = parsed(b);
    if (typeof va.seq === "number" && typeof vb.seq === "number") return va.seq - vb.seq;
    const rank = (r, v) => {
        if (r.namespace === "fs.read") return 0;
        return v.op === "write" ? 1 : v.op === "edit" ? 2 : 3;
    };
    return (rank(a, va) - rank(b, vb)) || String(a.value || "").localeCompare(String(b.value || ""));
}

// ── window rows → the promptEvents collectWindowOps expects ─────────────────
// Group the flat signal rows by event, mapping each row's value to an op.
// `rowFallback(eventId)` optionally supplies a friendlier endpoint name / ts /
// session from the scan window's trace rows (the signal rows only carry ids).
export function fsRowsToPromptEvents(rows, rowFallback) {
    const byEvent = new Map();
    // Deterministic replay order FIRST (see fsRowOrder) — reconstructFile is
    // order-sensitive and the wire order is arbitrary.
    const sorted = [...(rows || [])].sort(fsRowOrder);
    // Per-(event, namespace, tag) occurrence counter → unique op keys when one
    // event carries several ops on the same file (see fsValueToOp's `seq`).
    const seqs = new Map();
    for (const r of sorted) {
        const sk = `${r.event_id}:${r.namespace}:${r.tag}`;
        const seq = seqs.get(sk) || 0;
        seqs.set(sk, seq + 1);
        const op = fsValueToOp(r, seq);
        if (!op) continue;
        const id = String(r.event_id || "");
        if (!id) continue;
        let ev = byEvent.get(id);
        if (!ev) {
            const fb = rowFallback ? rowFallback(id) : null;
            ev = {
                promptId: id,
                sessionId: r.session_id || fb?.sessionId || "",
                endpointId: r.endpoint_id || fb?.endpointId || "",
                endpointName: fb?.endpointName || "",
                ts: fb?.timestamp ?? (r.event_time ? Date.parse(r.event_time) : null),
                ops: [],
            };
            byEvent.set(id, ev);
        }
        ev.ops.push(op);
    }
    return [...byEvent.values()];
}

// ── per-file line blame (fs.write tag lookup — no ILIKE blob scan) ──────────
// Given the fs.write rows for ONE file (tag = rpath(filePath)), oldest-first,
// return the first event whose write/edit actually authored the line. The tag
// already selects one exact path, but authoringOpFromOps re-checks the path
// suffix + line membership so attribution matches the reconstruction exactly.
export function pickFsAuthor(rows, line, targetPath) {
    // Same replay comparator as the tree path (fsRowOrder): a bare event_time
    // sort ties on same-event rows, and "who authored this line" is exactly
    // where picking the write/edit pair in the wrong order misattributes.
    const sorted = [...(rows || [])].sort(fsRowOrder);
    let scanned = 0;
    for (const r of sorted) {
        scanned += 1;
        const op = fsValueToOp(r);
        if (!op) continue;
        const hit = authoringOpFromOps([op], line, targetPath);
        if (hit) {
            return {
                hit: {
                    eventId: String(r.event_id),
                    sessionId: r.session_id ? String(r.session_id) : "",
                    ts: r.event_time || null,
                    kind: hit.kind,
                    path: hit.path,
                },
                scanned,
            };
        }
    }
    return { hit: null, scanned };
}

// ── per-tenant availability probe (gates the Files X-ray tab) ───────────────
// Mirror of hasMemorySignal: does this tenant have ANY `fs.write` data? The
// Files X-ray now reads the fs signal directly (manifest deploy_to: none, so
// per-tenant), with no hydration fallback, so the tab is hidden until this
// resolves true — otherwise it renders empty. Tenant-wide (apiScope []) over a
// wide fixed window (NOT the UI range) so narrowing time never hides a tenant
// that has fs data. Grouped by tag with limit 1: a metric-only COUNT always
// returns a row (n=0), so we group so an empty tenant yields zero rows.
const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
const TAG_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
const ST_EVENT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" };
// 90-day probe window — deliberately WIDER than signal_tags' 30-day TTL: the
// expired partitions are pruned (no extra cost), and the probe stays correct
// if retention ever widens. The TTL is the real bound on what it can find.
const FS_PROBE_RANGE = "1Q";

export async function hasFsSignal() {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: [], extraDims: ["signal"] });
    if (unsupported.length) return false;
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: "fs.write" } };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(FS_PROBE_RANGE)),
        filter: { andExpr: { children: [scopeFilter, nsGate].filter(Boolean) } },
        output: {
            dimensions: [{ field: TAG_FIELD, outputAlias: "tag" }],
            metrics: [{ outputAlias: "n", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        limit: 1,
    };
    try {
        const rows = rowsToObjects(await runAnalyticsQuery(spec));
        return rows.length > 0;
    } catch {
        return false;
    }
}
